import { jsx as _jsx } from "react/jsx-runtime";
import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { ColumnType } from '../api/generated';
import { formatValue, getFormatter, parseValue } from '../format/valueFormatters';
import { useSTContext } from '../table';
import { defineStyles } from '../theme';
const styles = defineStyles({
    textFieldInput: {
        minWidth: 180,
        '& .MuiInputBase-root': { pr: '0!important' },
        '& .MuiInput-input': { display: 'flex' },
        '& .MuiInputBase-hiddenLabel': { pt: 0 },
    },
    popperBox: { width: 'fit-content' },
});
const PROPAGATION_DEBOUNCE_MS = 350;
const valuePath = (and, or) => `andConditions.${and}.orConditions.${or}.value`;
const ConditionValueAutocomplete = ({ fieldName, control, orConditionsIndex, andConditionsIndex, dataType, columnFormat, operator, }) => {
    const intl = useIntl();
    const table = useSTContext();
    const hasFormatter = !!(columnFormat && operator && getFormatter(columnFormat));
    const value = useWatch({
        control,
        name: valuePath(andConditionsIndex, orConditionsIndex),
    });
    const [open, setOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectionJustHappened, setSelectionJustHappened] = useState(false);
    const [debouncedSearchValue] = useDebounce(searchValue, 500);
    const [debouncedForPropagation] = useDebounce(searchValue, PROPAGATION_DEBOUNCE_MS);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- parameter name in type definition
    const fieldRef = useRef(null);
    const { data, isFetching } = useQuery({
        queryKey: ['filter-value-autocomplete', fieldName, debouncedSearchValue],
        queryFn: () => table.api.autocompleteApiCall(fieldName, debouncedSearchValue?.trim() ?? ''),
        enabled: !!fieldName,
    });
    const isNumberType = dataType === ColumnType.Number;
    useEffect(() => {
        if (hasFormatter && isFocused && columnFormat && operator && !selectionJustHappened) {
            const parsed = parseValue(debouncedForPropagation, columnFormat, operator);
            if (parsed != null)
                fieldRef.current?.onChange(parsed);
        }
    }, [debouncedForPropagation, isFocused, hasFormatter, columnFormat, operator, selectionJustHappened]);
    const displayValue = hasFormatter
        ? isFocused
            ? searchValue
            : formatValue(value, columnFormat) ?? ''
        : undefined;
    return (_jsx(Controller, { control: control, name: valuePath(andConditionsIndex, orConditionsIndex), render: ({ field }) => {
            fieldRef.current = field;
            return (_jsx(Autocomplete, { PaperComponent: (props) => _jsx(Paper, { ...props, sx: styles.popperBox }), freeSolo: hasFormatter, blurOnSelect: true, open: open, onOpen: () => setOpen(true), onClose: () => setOpen(false), onFocus: () => {
                    setSelectionJustHappened(false);
                    if (hasFormatter) {
                        setIsFocused(true);
                        setSearchValue(formatValue(value, columnFormat) ?? '');
                    }
                }, onBlur: () => {
                    if (hasFormatter && columnFormat && operator && !selectionJustHappened) {
                        const parsed = parseValue(searchValue, columnFormat, operator);
                        if (parsed != null)
                            field.onChange(parsed);
                        setIsFocused(false);
                    }
                    setSelectionJustHappened(false);
                    field.onBlur();
                    setSearchValue('');
                }, loading: isFetching, options: data ?? [], inputValue: displayValue, onInputChange: (_, v) => setSearchValue(v), onChange: (_, newValue) => {
                    setSelectionJustHappened(true);
                    if (isNumberType && newValue) {
                        const num = Number(newValue);
                        field.onChange(!isNaN(num) ? num : newValue);
                    }
                    else {
                        field.onChange(newValue);
                    }
                }, getOptionLabel: (opt) => (hasFormatter && opt ? formatValue(String(opt), columnFormat) : String(opt ?? '')), forcePopupIcon: false, value: value?.toString() ?? null, clearIcon: false, noOptionsText: _jsx(FormattedMessage, { id: "autocomplete.no_options", defaultMessage: "No options" }), renderInput: (params) => (_jsx(Tooltip, { title: (hasFormatter ? formatValue(value, columnFormat) : value?.toString())?.trim() ?? null, children: _jsx("span", { children: _jsx(TextField, { ...params, variant: "outlined", type: isNumberType ? 'number' : 'text', onChange: (e) => {
                                const v = e.target.value;
                                if (!hasFormatter)
                                    setSearchValue(v);
                                if (isNumberType && v && !isNaN(Number(v)))
                                    field.onChange(Number(v));
                            }, onPaste: (e) => {
                                e.preventDefault();
                                const v = e.clipboardData.getData('text/plain').trim();
                                if (!hasFormatter)
                                    setSearchValue(v);
                                if (isNumberType && v && !isNaN(Number(v)))
                                    field.onChange(Number(v));
                            }, name: field.name, size: "small", sx: styles.textFieldInput, hiddenLabel: true, placeholder: intl.formatMessage({
                                id: 'tableFiltering.value.placeholder',
                                defaultMessage: 'Value',
                            }) }) }) })) }));
        } }));
};
export default ConditionValueAutocomplete;
//# sourceMappingURL=ConditionValueAutocomplete.js.map